import React, { useEffect, useState } from 'react'
import { useTheme, styled, Skeleton } from '@mui/material'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import Constants from '../../../../../constants/en.json'
import {
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
  Switch,
  Box,
} from '../../../../../components/mui/Core/index'
import { fetchPermissions, fetchPermissionsByRole } from './permissions-list-slice'
import NoResultsFoundTableOverlay from '../../../../../components/NoResultsFoundTableOverlay/no-results-found-table-overlay'

const IOSSwitch = styled((props) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <Switch focusVisibleClassName='.Mui-focusVisible' disableRipple {...props} />
))(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: theme.palette.primary.main,
        opacity: 1,
        border: 0,
        ...theme.applyStyles('dark', {
          backgroundColor: theme.palette.primary.main,
        }),
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: theme.palette.primary.main,
      border: '6px solid #fff',
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color: theme.palette.grey[100],
      ...theme.applyStyles('dark', {
        color: theme.palette.grey[600],
      }),
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: 0.7,
      ...theme.applyStyles('dark', {
        opacity: 0.3,
      }),
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 22,
    height: 22,
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    backgroundColor: '#E9E9EA',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500,
    }),
    ...theme.applyStyles('dark', {
      backgroundColor: '#39393D',
    }),
  },
}))

function PermissionsList({
  searchvalue,
  roleApplication,
  roleCode,
  isEditingRole,
  selectedPermissions,
  setSelectedPermissions,
}) {
  const theme = useTheme()
  const dispatch = useDispatch()
  const permissionsByApp = useSelector((state) => state?.permissions?.permissionsByApp)
  const isFetchingPermissionsByApp = useSelector(
    (state) => state?.permissions?.isFetchingPermissionsByApp,
  )
  const permissionsByRole = useSelector((state) => state?.permissions?.permissionsByRole)
  const [results, setResults] = useState([])

  useEffect(() => {
    if (roleApplication?.length > 0) {
      dispatch(fetchPermissions(roleApplication))
    }
  }, [dispatch, roleApplication])

  useEffect(() => {
    if (!isEditingRole) return
    if (!roleApplication || !roleCode) return
    dispatch(fetchPermissionsByRole({ appName: roleApplication, role: roleCode }))
  }, [roleApplication, roleCode, isEditingRole, dispatch])

  useEffect(() => {
    if (!permissionsByApp) return

    const searchResult = permissionsByApp?.filter((data) =>
      data?.description?.toLowerCase()?.includes(searchvalue?.toLowerCase()),
    )
    const filteredResult = searchResult?.filter((data) => data?.appName === roleApplication)

    setResults(filteredResult)
  }, [searchvalue, permissionsByApp, roleApplication])

  useEffect(() => {
    if (!permissionsByRole || !isEditingRole) return

    const filteredResult = permissionsByApp?.filter((data) => data?.appName === roleApplication)
    const enabledPermissions = permissionsByRole.map((p) => p.name)

    setSelectedPermissions(enabledPermissions)
    setResults(filteredResult)
  }, [permissionsByRole, isEditingRole, permissionsByApp, setSelectedPermissions, roleApplication])

  const handleTogglePermission = (permissionName) => {
    setSelectedPermissions((prev) => {
      if (prev.includes(permissionName)) {
        return prev.filter((name) => name !== permissionName)
      }
      return [...prev, permissionName]
    })
  }

  const permissionsListRootStyle = {
    width: '100%',
    bgcolor: 'background.paper',
    border: `1px solid ${theme.palette.shadow.dark}`,
    borderRadius: '4px',
    '& .MuiListItem-root:last-child': {
      borderBottom: 'none',
    },
  }
  const listStyle = { borderBottom: `1px solid ${theme.palette.shadow.dark}`, padding: '10px' }
  const notFoundResultStyle = {
    padding: '15px',
    display: 'block',
  }

  return (
    <List sx={permissionsListRootStyle}>
      <Typography variant='h6' sx={listStyle}>
        {Constants.roleManagement.createRole.PERMISSIONS}
      </Typography>

      {/* CASE 1: No permissions for this application */}
      {!isFetchingPermissionsByApp &&
        permissionsByApp?.length > 0 &&
        results?.length === 0 &&
        searchvalue === '' && (
          <Typography sx={notFoundResultStyle} variant='message' align='center'>
            {Constants.roleManagement.createRole.NO_PERMISSIONS_AVAILABLE_BY_APPLICATION}
          </Typography>
        )}

      {/* CASE 2: Search performed but found nothing */}
      {!isFetchingPermissionsByApp &&
        permissionsByApp?.length > 0 &&
        results?.length === 0 &&
        searchvalue !== '' && (
          <Box sx={notFoundResultStyle}>
            <NoResultsFoundTableOverlay
              filterByDescription='permission'
              noResultsValue={searchvalue}
            />
          </Box>
        )}

      {isFetchingPermissionsByApp && (
        <>
          {[1, 2, 3, 4, 5, 6].map((key) => {
            return (
              <ListItem
                key={`skeleton-list-${key}`}
                disableGutters
                secondaryAction={
                  <IconButton aria-label='permissions'>
                    <Skeleton variant='circular' width={30} height={30} />
                  </IconButton>
                }
                sx={listStyle}
              >
                <ListItemText primary={<Skeleton variant='text' width='60%' height={20} />} />
              </ListItem>
            )
          })}
        </>
      )}

      {results?.map((permission) => (
        <ListItem
          key={permission.name}
          disableGutters
          secondaryAction={
            <IconButton aria-label='permissions'>
              <IOSSwitch
                checked={selectedPermissions.includes(permission.name)}
                onChange={() => handleTogglePermission(permission.name)}
              />
            </IconButton>
          }
          sx={listStyle}
        >
          <ListItemText primary={`${permission.description}`} />
        </ListItem>
      ))}
    </List>
  )
}

PermissionsList.propTypes = {
  searchvalue: PropTypes.string,
  roleApplication: PropTypes.string,
  roleCode: PropTypes.string,
  isEditingRole: PropTypes.bool,
  selectedPermissions: PropTypes.arrayOf(PropTypes.string),
  setSelectedPermissions: PropTypes.func,
}

export default PermissionsList
